import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { invalidateReviewQueue } from 'components/ReviewQueue/reviewQueueKey';
import { FormProvider, useForm } from 'react-hook-form';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { FormModal } from '@/components/ui/form-modal';
import FormGeoLocationField from './formFields/FormGeoLocationField';
import FormSymbologySelect from 'components/MapPage/CorporateViewTabs/LayersTab/FormSymbologySelect';
import {
  ActionAssigneeKind,
  DEFAULT_ACTION_PRIORITY,
  IapScope,
  INCIDENT,
  SUBMITTED_FOR_REVIEW_STATUS,
} from './ICSConstants';
import ICSChecklist from './ICSChecklist';
import {
  checklistForSubmit,
  normalizeChecklistItems,
  pruneChecklistResponsibleIndividuals,
  type ActionChecklistItem,
} from './actionChecklistModel';
import { ActionBase } from '../../types/iapBaseTypes';
import type { GeoLocation } from 'components/GeoLocationComponent/buildGeoLocationFromGeoJson';
import {
  ActionEntity,
  ActionFormValues,
  useScopedActionForm,
} from './hooks/useScopedActionForm';
import { useIncidentObjectives } from './hooks/incidentObjectiveHooks';
import {
  keys as incidentActionKeys,
  useIncidentActionVersions,
  type GetIncidentActionResponse,
} from './hooks/incidentActionHooks';
import { TaskVersionBanner } from './actionTicket/TaskVersionBanner';
import { TaskApprovalRouteDialog } from './actionTicket/TaskApprovalRouteDialog';
import { TaskApproveExecutionDialog } from './actionTicket/TaskApproveExecutionDialog';
import { TaskRejectExecutionDialog } from './actionTicket/TaskRejectExecutionDialog';
import {
  canSetCurrentOngoing,
  isApproveExecuteCtaKind,
  nextTaskApprovalCta,
  parseTaskSignatures,
  signaturesAfterRejectCompletion,
  type TaskSignatureSlot,
} from './actionTicket/taskSignatureSlots';
import Ics204SignDialog from './planningView/widgets/AssignmentList/Ics204SignDialog';
import type { WalaSignature } from './planningView/widgets/AssignmentList/ics204SignatureSlots';
import { useQueryClient } from '@tanstack/react-query';
import { useAors } from 'hooks/useAors';
import { useActualIncidentId } from './hooks/commonHooks';
import { useScopedIncident } from './hooks/incidentHooks';
import { useActionTicketsFlag, useHwcgFlag } from 'utils/featureFlags';
import ActionFormFields from './ActionFormFields';
import {
  DEFAULT_ACTION_CLASSIFICATION,
  normalizeActionClassification,
} from './actionClassification';
import { ActionTicketBody } from './actionTicket/ActionTicketBody';
import PositionFilesSection from './actionTicket/PositionFilesSection';
import { TaskFieldEditProvider } from './actionTicket/TaskFieldEditContext';
import {
  mergePersistOverrides,
  tipRowFromUpdateResult,
  type TaskPersistOverrides,
} from './actionTicket/taskFieldEditModel';
import {
  attachmentsForSubmit,
  commentsForSubmit,
  dailyProgressReportsForSubmit,
  normalizeDailyProgressReports,
  type DailyProgressReport,
  hazardsForSubmit,
  normalizeAttachments,
  normalizeComments,
  normalizeHazards,
  normalizeRequirements,
  requirementsForSubmit,
  type ActionAttachment,
  type ActionHazard,
  type ActionResourceRequirement,
  type TaskComment,
} from './actionTicket/actionTicketModel';
import { useExportedFormFiles } from 'components/FileManagement/hooks/useExportedFormFiles';
import TaskReportLinks from 'components/Reports/tasks/TaskReportLinks';
import {
  pruneResponsibleIndividualIds,
  responsibleIndividualDisplayOptions,
  responsibleIndividualOptions,
} from './hooks/responsibleIndividuals';
import {
  selectUser,
  selectUserGuid,
  useAppSelector,
} from 'slices/commonSelectors';

type FormFields = ActionBase & {
  poc: string;
  briefed_poc: boolean;
  start_date: Date;
  status: string;
  priority: string;
  assignee_kind: ActionAssigneeKind;
  assignee_id: string | null;
  classification: string;
  symbology: string;
  target_date: Date;
  actual_date: Date;
  resources: string[];
  geolocation?: GeoLocation | null;
  aor_id?: string | null;
  parent_id?: string | null;
  reporting_location?: string | null;
  requested_arrival_time?: Date | string | null;
};

const ASSIGNEE_KIND_LABEL: Record<ActionAssigneeKind, string> = {
  user: 'Person',
  team: 'Team',
  position: 'ICS Position',
  role: 'Role',
};

/** Actions written before the assignee model existed only carry `poc`. */
function initialAssigneeKind(entity?: ActionEntity): ActionAssigneeKind {
  const kind = (entity as any)?.assignee_kind as
    | ActionAssigneeKind
    | null
    | undefined;
  return kind ?? 'user';
}

function formValuesFromEntity(entity: ActionEntity): FormFields {
  const row = entity as any;
  return {
    ...row,
    name: entity.name ?? '',
    description: entity.description ?? '',
    priority: row.priority || DEFAULT_ACTION_PRIORITY,
    assignee_kind: initialAssigneeKind(entity),
    assignee_id: row.assignee_id ?? null,
    classification: normalizeActionClassification(row.classification),
    symbology: row.symbology ?? '',
    resources: row.resources || [],
    geolocation: row.geolocation || null,
    aor_id: row.aor_id ?? null,
    parent_id: row.parent_id ?? '',
    reporting_location: row.reporting_location ?? '',
    requested_arrival_time: row.requested_arrival_time ?? null,
    poc: row.poc ?? '',
    briefed_poc: Boolean(row.briefed_poc),
    status: row.status || 'Planned',
  };
}

type ActionDrawerProps = {
  scope: IapScope;
  selectedEntity?: ActionEntity;
  toggle: () => void;
  /** When set, create/update against this incident instead of route/Redux. */
  incidentIdOverride?: string | null;
  /** Prefill AOR when creating from an AOR-scoped report. */
  defaultAorId?: string | null;
  /** Prefill status when the surface creating the task implies one. */
  defaultStatus?: string | null;
  /** Prefill the assigned person, e.g. the sole occupant of a roster seat. */
  defaultAssigneeId?: string | null;
  /** Prefill who the task is assigned to. Defaults to a person. */
  defaultAssigneeKind?: ActionAssigneeKind;
  /** Roster seat this drawer was opened from, used by the 213RR back button. */
  positionName?: string;
  /** Called with the new task's stable action_id after a create. */
  onCreated?: (actionId: string) => void;
};

function createdActionId(result: unknown): string | null {
  const row = result as
    | { action_id?: unknown; data?: { action_id?: unknown } }
    | null
    | undefined;
  const id = row?.data?.action_id ?? row?.action_id;
  return typeof id === 'string' && id ? id : null;
}

const ActionDrawer: React.FC<ActionDrawerProps> = ({
  scope,
  selectedEntity,
  toggle,
  incidentIdOverride,
  defaultAorId,
  defaultStatus,
  defaultAssigneeId,
  defaultAssigneeKind = 'user',
  positionName,
  onCreated,
}) => {
  const isEdit = !!selectedEntity;
  /** Tip row id drifts after each append-only PUT; keep the write target fresh. */
  const [tipEntity, setTipEntity] = useState<ActionEntity | undefined>(
    selectedEntity
  );
  useEffect(() => {
    // Reset tip only when opening a different task — not when the tip id advances.
    setTipEntity(selectedEntity);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- action_id gate
  }, [selectedEntity?.action_id]);
  const writeEntity = tipEntity ?? selectedEntity;
  const tickets = useActionTicketsFlag();
  const { data: aors = [] } = useAors();
  const incident = useScopedIncident();
  const routeIncidentId = useActualIncidentId();
  const isIncidentScope = scope === INCIDENT;
  const workspaceLabel = isIncidentScope
    ? (incident as { name?: string } | null)?.name || 'This incident'
    : '';
  const incidentId = isIncidentScope
    ? incidentIdOverride || routeIncidentId || undefined
    : undefined;
  const adapter = useScopedActionForm(
    scope,
    incidentId,
    selectedEntity?.action_id
  );

  const form = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: isEdit
      ? formValuesFromEntity(selectedEntity as ActionEntity)
      : {
          status: defaultStatus || 'Planned',
          priority: DEFAULT_ACTION_PRIORITY,
          assignee_kind: defaultAssigneeKind,
          // `poc` is the registered field for a person, and what submit reads
          // for a user assignment; `assignee_id` is kept in step with it.
          poc:
            defaultAssigneeKind === 'user'
              ? (defaultAssigneeId ?? undefined)
              : undefined,
          assignee_id: defaultAssigneeId ?? null,
          classification: DEFAULT_ACTION_CLASSIFICATION,
          symbology: '',
          resources: [],
          geolocation: null,
          description: '',
          aor_id: defaultAorId ?? null,
          parent_id: '',
          reporting_location: '',
          requested_arrival_time: null,
        },
  });
  const { handleSubmit, watch, reset, getValues } = form;
  const watchedName = watch('name');
  const watchedStatus = watch('status');
  const assigneeKind = watch('assignee_kind') ?? 'user';
  const watchedAssigneeId = watch('assignee_id');
  const hwcg = useHwcgFlag();
  const queryClient = useQueryClient();
  const currentUserGuid = useAppSelector(selectUserGuid);
  const currentUser = useAppSelector(selectUser);
  const currentUserName = currentUser?.user_name;
  const defaultSignName =
    currentUserName?.trim() || currentUser?.email_address?.trim() || '';
  const showVersionBar = hwcg && isIncidentScope && isEdit;
  const stableActionId =
    (selectedEntity as GetIncidentActionResponse | undefined)?.action_id ||
    undefined;
  const versionsQuery = useIncidentActionVersions(
    incidentId,
    stableActionId,
    showVersionBar && !!stableActionId
  );
  const versions = versionsQuery.data ?? [];
  const bannerVersions =
    versions.length > 0
      ? versions
      : selectedEntity && stableActionId
        ? [selectedEntity as GetIncidentActionResponse]
        : [];

  // Position-tagged folders only make sense against an incident's org chart.
  const positionFiles =
    hwcg && isIncidentScope && incidentId ? (
      <PositionFilesSection
        incidentId={incidentId}
        assigneeKind={assigneeKind}
        assigneeId={watch('assignee_id')}
        poc={watch('poc')}
      />
    ) : null;

  const assigneeKindOptions = [
    { label: ASSIGNEE_KIND_LABEL.user, value: 'user' },
    ...(adapter.teamOptions.length
      ? [{ label: ASSIGNEE_KIND_LABEL.team, value: 'team' }]
      : []),
    ...(adapter.positionOptions.length
      ? [{ label: ASSIGNEE_KIND_LABEL.position, value: 'position' }]
      : []),
  ];

  /**
   * Normalized rather than mapped inline so per-item notes and attachments
   * survive an edit: the old map rebuilt each row from label and checked alone,
   * which would have dropped them on the next save.
   */
  const [checklist, setChecklist] = useState<ActionChecklistItem[]>(() =>
    normalizeChecklistItems(selectedEntity?.checklist)
  );
  const [dependsOn, setDependsOn] = useState<string[]>(
    (selectedEntity as any)?.depends_on ?? []
  );
  const [responsibleIndividualIds, setResponsibleIndividualIds] = useState<
    string[]
  >((selectedEntity as any)?.responsible_individual_ids ?? []);
  const occupantOptions = useMemo(
    () =>
      isIncidentScope
        ? responsibleIndividualOptions(adapter.roster, watchedAssigneeId)
        : [],
    [adapter.roster, isIncidentScope, watchedAssigneeId]
  );
  const responsibleOptions = useMemo(
    () =>
      responsibleIndividualDisplayOptions(
        occupantOptions,
        responsibleIndividualIds,
        adapter.pocOptions
      ),
    [adapter.pocOptions, occupantOptions, responsibleIndividualIds]
  );
  const assigneeSelectionRef = useRef({
    kind: assigneeKind,
    id: watchedAssigneeId ?? null,
  });
  useEffect(() => {
    const prev = assigneeSelectionRef.current;
    const nextId = watchedAssigneeId ?? null;
    assigneeSelectionRef.current = { kind: assigneeKind, id: nextId };
    if (prev.kind === assigneeKind && prev.id === nextId) return;
    if (assigneeKind !== 'position') {
      setResponsibleIndividualIds([]);
      return;
    }
    setResponsibleIndividualIds((current) =>
      pruneResponsibleIndividualIds(current, occupantOptions)
    );
  }, [assigneeKind, occupantOptions, watchedAssigneeId]);
  useEffect(() => {
    if (!isIncidentScope || assigneeKind !== 'position' || !adapter.roster) {
      return;
    }
    setChecklist((current) => {
      const next = pruneChecklistResponsibleIndividuals(
        current,
        occupantOptions.map((option) => option.value)
      );
      return next;
    });
  }, [adapter.roster, assigneeKind, isIncidentScope, occupantOptions]);
  /**
   * The three row editors are local state rather than form fields, matching the
   * checklist: they are arrays of rows, not registered inputs. They are seeded
   * and re-submitted even with the flag off, so a teammate's ticket rows
   * survive an edit made on the legacy form.
   */
  const [requirements, setRequirements] = useState<ActionResourceRequirement[]>(
    () => normalizeRequirements((selectedEntity as any)?.resource_requirements)
  );
  const [requirementsNa, setRequirementsNa] = useState(() =>
    Boolean((selectedEntity as any)?.resource_requirements_na)
  );
  const [hazards, setHazards] = useState<ActionHazard[]>(() =>
    normalizeHazards((selectedEntity as any)?.hazards)
  );
  const [attachments, setAttachments] = useState<ActionAttachment[]>(() =>
    normalizeAttachments((selectedEntity as any)?.attachments)
  );
  const [comments, setComments] = useState<TaskComment[]>(() =>
    normalizeComments((selectedEntity as any)?.comments)
  );
  const [dailyProgressReports, setDailyProgressReports] = useState<
    DailyProgressReport[]
  >(() =>
    normalizeDailyProgressReports(
      (selectedEntity as any)?.daily_progress_reports
    )
  );
  const [signatures, setSignatures] = useState<WalaSignature[]>(() =>
    parseTaskSignatures((selectedEntity as any)?.signatures)
  );
  const [routeOpen, setRouteOpen] = useState(false);
  const [approveExecuteOpen, setApproveExecuteOpen] = useState(false);
  const [rejectKind, setRejectKind] = useState<
    'execution' | 'completion' | null
  >(null);
  const [signOpen, setSignOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const viewing = versions.find((row) => row.id === viewingId) ?? null;
  const approvalCta = nextTaskApprovalCta(signatures, watchedStatus);

  const { data: exportedForms = [] } = useExportedFormFiles(incidentId);
  const formOptions = exportedForms.map((file) => ({
    id: String(file.id),
    name: file.file_name,
    fileUrl: file.fileUrl,
    mimetype: file.mimetype,
  }));

  const displayId =
    (selectedEntity as (ActionEntity & { display_id?: string }) | undefined)
      ?.display_id ?? null;

  const valuesFromForm = (formData: FormFields): ActionFormValues => {
    // Send only the arm of the assignee the user actually picked; coms
    // reconciles it with `poc` (see actionTaskFields.ts) so a stale value from
    // the other arm cannot resurrect an old assignment.
    const isUserAssignment = assigneeKind === 'user';
    return {
      ...(formData as any),
      checklist: checklistForSubmit(checklist),
      depends_on: dependsOn,
      assignee_kind: assigneeKind,
      assignee_id: isUserAssignment
        ? (formData.poc ?? null)
        : (formData.assignee_id ?? null),
      poc: isUserAssignment ? (formData.poc ?? null) : null,
      responsible_individual_ids:
        isIncidentScope && assigneeKind === 'position'
          ? responsibleIndividualIds
          : [],
      resource_requirements: requirementsNa
        ? []
        : requirementsForSubmit(requirements),
      resource_requirements_na: requirementsNa,
      hazards: hazardsForSubmit(hazards),
      attachments: attachmentsForSubmit(attachments),
      comments: commentsForSubmit(comments),
      daily_progress_reports:
        dailyProgressReportsForSubmit(dailyProgressReports),
      signatures,
    };
  };

  const applySnapshot = (row: GetIncidentActionResponse) => {
    reset(formValuesFromEntity(row));
    setChecklist(normalizeChecklistItems(row.checklist));
    setRequirements(normalizeRequirements(row.resource_requirements));
    setRequirementsNa(Boolean(row.resource_requirements_na));
    setHazards(normalizeHazards(row.hazards));
    setAttachments(normalizeAttachments(row.attachments));
    setComments(normalizeComments(row.comments));
    setDailyProgressReports(
      normalizeDailyProgressReports(row.daily_progress_reports)
    );
    setSignatures(parseTaskSignatures(row.signatures));
    setResponsibleIndividualIds(row.responsible_individual_ids ?? []);
  };

  const invalidateVersions = () => {
    if (!writeEntity || !incidentId) return;
    void queryClient.invalidateQueries({
      queryKey: incidentActionKeys.versions(incidentId, writeEntity.action_id),
    });
  };

  const syncTipFromResult = (result: unknown) => {
    const row = tipRowFromUpdateResult(result);
    if (!row || !writeEntity) return;
    setTipEntity({
      ...(writeEntity as ActionEntity),
      ...(row as Partial<ActionEntity>),
      id: String(row.id),
      action_id: String(row.action_id ?? writeEntity.action_id),
    } as ActionEntity);
  };

  const autoPersist = hwcg && isEdit && !viewing;

  const persistTip = useCallback(
    async (overrides?: TaskPersistOverrides) => {
      if (!autoPersist || !writeEntity) return;
      const values = mergePersistOverrides(
        valuesFromForm(getValues()) as unknown as Record<string, unknown>,
        overrides
      ) as ActionFormValues;
      const result = await adapter.update(writeEntity, values);
      syncTipFromResult(result);
      invalidateVersions();
    },
    // valuesFromForm closes over local tip state; refresh when those change.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional tip snapshot
    [
      adapter,
      autoPersist,
      checklist,
      comments,
      dailyProgressReports,
      dependsOn,
      getValues,
      hazards,
      attachments,
      requirements,
      requirementsNa,
      responsibleIndividualIds,
      signatures,
      writeEntity,
      viewing,
      hwcg,
      isEdit,
      isIncidentScope,
      assigneeKind,
    ]
  );

  const appendTaskSignature = async (
    slot: TaskSignatureSlot,
    name: string,
    statusOverride?: string
  ) => {
    if (!writeEntity || viewing) return;
    const stamp: WalaSignature = {
      name: name.trim(),
      role: slot.role,
      signedAt: Date.now(),
      ...(currentUserGuid ? { userGuid: currentUserGuid } : {}),
    };
    const next = [...signatures, stamp];
    setSignatures(next);
    const status =
      statusOverride ??
      (slot.id === 'request-execute'
        ? SUBMITTED_FOR_REVIEW_STATUS
        : slot.id === 'approve-completion'
          ? 'Closed'
          : getValues().status);
    const result = await adapter.update(writeEntity, {
      ...valuesFromForm(getValues()),
      signatures: next,
      status,
    });
    syncTipFromResult(result);
    if (status !== getValues().status) {
      reset({ ...getValues(), status });
    }
    invalidateVersions();
    invalidateReviewQueue(queryClient);
  };

  const postComments = async (next: TaskComment[]) => {
    setComments(next);
    if (!writeEntity || viewing) return;
    const result = await adapter.update(writeEntity, {
      ...valuesFromForm(getValues()),
      comments: commentsForSubmit(next),
    });
    syncTipFromResult(result);
    invalidateVersions();
  };

  const commitDailyProgressReports = async (next: DailyProgressReport[]) => {
    setDailyProgressReports(next);
    if (autoPersist) {
      await persistTip({
        daily_progress_reports: dailyProgressReportsForSubmit(next),
      });
    }
  };

  const onSubmit = async (formData: FormFields) => {
    if (viewing) return;
    const values = valuesFromForm(formData);
    if (isEdit) {
      if (!writeEntity) return;
      await adapter.update(writeEntity, values);
    } else {
      const created = await adapter.create(values);
      const actionId = createdActionId(created);
      if (actionId) onCreated?.(actionId);
    }
    toggle();
  };

  /** Fully signed: new tip reset to Planned with cleared signatures. */
  const draftNewVersion = async () => {
    if (!writeEntity || viewing) return;
    setSignatures([]);
    const result = await adapter.update(writeEntity, {
      ...valuesFromForm(getValues()),
      status: 'Planned',
      signatures: [],
    });
    syncTipFromResult(result);
    reset({ ...getValues(), status: 'Planned' });
    invalidateVersions();
    invalidateReviewQueue(queryClient);
  };

  /** Reject Completion: drop Request Completion stamp; stay Current/Ongoing. */
  const rollbackCompletionRequest = async () => {
    if (!writeEntity || viewing) return;
    const next = signaturesAfterRejectCompletion(signatures);
    setSignatures(next);
    const result = await adapter.update(writeEntity, {
      ...valuesFromForm(getValues()),
      status: 'Current/Ongoing',
      signatures: next,
    });
    syncTipFromResult(result);
    reset({ ...getValues(), status: 'Current/Ongoing' });
    invalidateVersions();
    invalidateReviewQueue(queryClient);
  };

  const beginExecution = async () => {
    if (!writeEntity || viewing) return;
    const result = await adapter.update(writeEntity, {
      ...valuesFromForm(getValues()),
      status: 'Current/Ongoing',
      signatures,
    });
    syncTipFromResult(result);
    reset({ ...getValues(), status: 'Current/Ongoing' });
    invalidateVersions();
    invalidateReviewQueue(queryClient);
  };

  const openApprovalCta = () => {
    if (!approvalCta || viewing) return;
    if (approvalCta.kind === 'begin-execute') {
      void beginExecution();
      return;
    }
    if (isApproveExecuteCtaKind(approvalCta.kind)) {
      setApproveExecuteOpen(true);
      return;
    }
    if (approvalCta.slot?.requiresTypedName) {
      setSignOpen(true);
      return;
    }
    setRouteOpen(true);
  };

  const confirmRouteSignature = async () => {
    if (!approvalCta?.slot || approvalCta.slot.requiresTypedName) return;
    const name = defaultSignName || 'Unknown';
    await appendTaskSignature(approvalCta.slot, name);
  };

  const confirmSign = async (name: string) => {
    if (!approvalCta?.slot?.requiresTypedName) return;
    const slot = approvalCta.slot;
    await appendTaskSignature(slot, name);
    setSignOpen(false);
  };

  const handleDeactivateClick = () => {
    if (!selectedEntity?.archived) {
      setShowConfirmModal(true);
    } else {
      void toggleActive();
    }
  };

  const toggleActive = async () => {
    if (writeEntity) {
      await adapter.archive(writeEntity, !writeEntity.archived);
    }
    setShowConfirmModal(false);
    toggle();
  };

  const commitChecklist = (next: ActionChecklistItem[]) => {
    setChecklist(next);
    if (autoPersist) {
      void persistTip({ checklist: checklistForSubmit(next) });
    }
  };

  const commitRequirements = (next: ActionResourceRequirement[]) => {
    setRequirements(next);
    if (autoPersist) {
      void persistTip({
        resource_requirements: requirementsNa
          ? []
          : requirementsForSubmit(next),
        resource_requirements_na: requirementsNa,
      });
    }
  };

  const commitRequirementsNa = (next: boolean) => {
    setRequirementsNa(next);
    if (autoPersist) {
      void persistTip({
        resource_requirements: next ? [] : requirementsForSubmit(requirements),
        resource_requirements_na: next,
      });
    }
  };

  const commitHazards = (next: ActionHazard[]) => {
    setHazards(next);
    if (autoPersist) {
      void persistTip({ hazards: hazardsForSubmit(next) });
    }
  };

  const commitAttachments = (next: ActionAttachment[]) => {
    setAttachments(next);
    if (autoPersist) {
      void persistTip({ attachments: attachmentsForSubmit(next) });
    }
  };

  const aorOptions = aors.map((aor) => ({ label: aor.name, value: aor.id }));
  const { data: incidentObjectives = [] } = useIncidentObjectives(scope);
  const objectiveOptions = incidentObjectives
    .filter((objective) => !objective.archived)
    .map((objective) => ({
      value: objective.objective_id,
      label: objective.name,
    }));
  const showObjective = isIncidentScope;

  return (
    <>
      <FormProvider {...form}>
        <TaskFieldEditProvider
          autoPersist={autoPersist}
          persistTip={persistTip}
        >
          <FormModal
            open
            onOpenChange={(open) => {
              if (!open) toggle();
            }}
            title={isEdit ? (hwcg ? 'Edit Task' : 'Edit Action') : 'Add Task'}
            widthClassName={
              tickets
                ? 'tw:!max-w-6xl tw:sm:!max-w-6xl'
                : 'tw:!max-w-2xl tw:sm:!max-w-2xl'
            }
            formProps={{ onSubmit: handleSubmit(onSubmit) }}
            banner={
              showVersionBar ? (
                <TaskVersionBanner
                  versions={bannerVersions}
                  viewing={viewing}
                  status={viewing ? viewing.status : watchedStatus}
                  signatures={
                    viewing
                      ? parseTaskSignatures(viewing.signatures)
                      : signatures
                  }
                  saving={adapter.isSaving}
                  onView={(row) => {
                    const snapshot = bannerVersions.find(
                      (item) => item.id === row.id
                    );
                    if (!snapshot) return;
                    setViewingId(snapshot.id);
                    applySnapshot(snapshot);
                  }}
                  onViewLatest={() => {
                    setViewingId(null);
                    const tip =
                      bannerVersions[0] ?? tipEntity ?? selectedEntity;
                    if (tip) {
                      applySnapshot(tip as GetIncidentActionResponse);
                      setTipEntity(tip as ActionEntity);
                    }
                  }}
                  onApprovalCta={openApprovalCta}
                  onRejectExecution={() => setRejectKind('execution')}
                  onRejectCompletion={() => setRejectKind('completion')}
                  onDraftNewVersion={() => {
                    void draftNewVersion();
                  }}
                />
              ) : null
            }
            footer={
              <div className="tw:flex tw:flex-wrap tw:justify-end tw:gap-2">
                {/* With the ticket on, deactivate lives in its header. */}
                {isEdit && !tickets && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleDeactivateClick}
                  >
                    {(!writeEntity?.archived && 'Deactivate') || 'Activate'}
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={toggle}>
                  Close
                </Button>
                {hwcg && isEdit ? null : (
                  <Button
                    type="submit"
                    disabled={
                      Boolean(viewing) ||
                      (!isEdit && !watchedName) ||
                      adapter.isSaving
                    }
                  >
                    Save
                  </Button>
                )}
              </div>
            }
          >
            <fieldset
              disabled={Boolean(viewing)}
              className="tw:m-0 tw:min-w-0 tw:border-0 tw:p-0"
            >
              {isEdit ? (
                <TaskReportLinks
                  actionId={
                    selectedEntity?.action_id
                      ? String(selectedEntity.action_id)
                      : null
                  }
                  className="tw:mb-3"
                />
              ) : null}
              {tickets ? (
                <ActionTicketBody
                  entity={writeEntity}
                  displayId={displayId}
                  archived={writeEntity?.archived}
                  onArchiveClick={isEdit ? handleDeactivateClick : undefined}
                  assigneeKind={assigneeKind}
                  assigneeKindOptions={assigneeKindOptions}
                  pocOptions={adapter.pocOptions}
                  teamOptions={adapter.teamOptions}
                  positionOptions={adapter.positionOptions}
                  aorOptions={aorOptions}
                  objectiveOptions={objectiveOptions}
                  showObjective={showObjective}
                  workspaceLabel={workspaceLabel}
                  dependencyChoices={adapter.dependencyChoices}
                  dependsOn={dependsOn}
                  onDependsOnChange={setDependsOn}
                  responsibleIndividualOptions={responsibleOptions}
                  responsibleIndividualIds={responsibleIndividualIds}
                  onResponsibleIndividualIdsChange={setResponsibleIndividualIds}
                  assigneeId={watchedAssigneeId}
                  showClassification={adapter.showClassification}
                  requirements={requirements}
                  onRequirementsChange={commitRequirements}
                  requirementsNa={requirementsNa}
                  onRequirementsNaChange={commitRequirementsNa}
                  hazards={hazards}
                  onHazardsChange={commitHazards}
                  attachments={attachments}
                  onAttachmentsChange={commitAttachments}
                  formOptions={formOptions}
                  incidentId={incidentId}
                  positionName={
                    positionName ||
                    (assigneeKind === 'position'
                      ? adapter.positionOptions.find(
                          (option) => option.value === watchedAssigneeId
                        )?.label
                      : undefined)
                  }
                  comments={comments}
                  onCommentPost={postComments}
                  commentsReadOnly={Boolean(viewing)}
                  dailyProgressReports={dailyProgressReports}
                  onDailyProgressReportsChange={commitDailyProgressReports}
                  currentUserGuid={currentUserGuid}
                  currentUserName={currentUserName}
                  disableCurrentOngoing={!canSetCurrentOngoing(signatures)}
                  positionFiles={positionFiles}
                  subTasks={
                    hwcg ? (
                      <ICSChecklist
                        checklist={checklist}
                        setChecklist={commitChecklist}
                        editable={!viewing}
                        existingData={isEdit}
                        incidentId={incidentId}
                        asSection
                        responsibleIndividualOptions={occupantOptions}
                        currentUserGuid={currentUserGuid}
                        currentUserName={currentUserName}
                      />
                    ) : null
                  }
                  extraFields={
                    hwcg ? null : (
                      <>
                        <div className="tw:space-y-1.5">
                          <FormSymbologySelect
                            variant="shadcn"
                            onSymbologyChange={() => {
                              if (autoPersist) void persistTip();
                            }}
                          />
                          <p className="tw:m-0 tw:text-[0.675rem] tw:text-muted-foreground">
                            The symbol this task draws with on the map. Leave as
                            None to keep the status and priority marker.
                          </p>
                        </div>
                        <div className="tw:space-y-1.5">
                          <div className="tw:text-xs tw:font-semibold tw:tracking-wide tw:uppercase tw:text-muted-foreground">
                            Location
                          </div>
                          <FormGeoLocationField
                            name="geolocation"
                            label=""
                            onCommit={() => {
                              if (autoPersist) void persistTip();
                            }}
                          />
                        </div>
                        <ICSChecklist
                          checklist={checklist}
                          setChecklist={setChecklist}
                          editable={!viewing}
                          existingData={isEdit}
                          incidentId={incidentId}
                          asSection
                          responsibleIndividualOptions={occupantOptions}
                        />
                      </>
                    )
                  }
                />
              ) : (
                <ActionFormFields
                  isEdit={isEdit}
                  selectedEntity={writeEntity}
                  displayId={displayId}
                  workspaceLabel={workspaceLabel}
                  assigneeKind={assigneeKind}
                  assigneeKindOptions={assigneeKindOptions}
                  aorOptions={aorOptions}
                  objectiveOptions={objectiveOptions}
                  showObjective={showObjective}
                  pocOptions={adapter.pocOptions}
                  teamOptions={adapter.teamOptions}
                  positionOptions={adapter.positionOptions}
                  dependencyChoices={adapter.dependencyChoices}
                  dependsOn={dependsOn}
                  onDependsOnChange={setDependsOn}
                  responsibleIndividualOptions={responsibleOptions}
                  responsibleIndividualIds={responsibleIndividualIds}
                  onResponsibleIndividualIdsChange={setResponsibleIndividualIds}
                  assigneeId={watchedAssigneeId}
                  showClassification={adapter.showClassification}
                  checklist={checklist}
                  setChecklist={setChecklist}
                  subTaskResponsibleOptions={occupantOptions}
                  incidentId={incidentId}
                  positionFiles={positionFiles}
                />
              )}
            </fieldset>
          </FormModal>
        </TaskFieldEditProvider>
      </FormProvider>

      {stableActionId ? (
        <TaskApprovalRouteDialog
          open={routeOpen}
          title={approvalCta?.label ?? 'Request Approval'}
          actionId={stableActionId}
          taskName={watchedName || ''}
          onOpenChange={setRouteOpen}
          confirmPending={adapter.isSaving}
          onConfirm={async () => {
            await confirmRouteSignature();
          }}
        />
      ) : null}

      {stableActionId ? (
        <TaskApproveExecutionDialog
          open={approveExecuteOpen}
          actionId={stableActionId}
          taskName={watchedName || ''}
          defaultName={defaultSignName}
          title={approvalCta?.label ?? 'Approve Execution'}
          onOpenChange={setApproveExecuteOpen}
          confirmPending={adapter.isSaving}
          onConfirmSign={async (name) => {
            const slot = approvalCta?.slot;
            if (!slot || !isApproveExecuteCtaKind(slot.id)) return;
            await appendTaskSignature(slot, name);
          }}
        />
      ) : null}

      {stableActionId ? (
        <TaskRejectExecutionDialog
          open={rejectKind != null}
          actionId={stableActionId}
          taskName={watchedName || ''}
          defaultRequesterUserId={
            rejectKind === 'completion'
              ? (signatures[3]?.userGuid ?? signatures[0]?.userGuid ?? null)
              : (signatures[0]?.userGuid ?? null)
          }
          onOpenChange={(open) => {
            if (!open) setRejectKind(null);
          }}
          confirmPending={adapter.isSaving}
          onConfirmDraft={async () => {
            if (rejectKind === 'completion') {
              await rollbackCompletionRequest();
              return;
            }
            await draftNewVersion();
          }}
          {...(rejectKind === 'completion'
            ? {
                title: 'Reject Completion',
                description:
                  'Rejecting removes the Request Completion Approval stamp and returns the task to Current/Ongoing so completion approval must be requested again. Task content and execution approvals are kept.',
                messagePlaceholder: 'Explain why completion was rejected…',
                confirmLabel: 'Reject Completion',
              }
            : {})}
        />
      ) : null}

      {approvalCta?.slot?.requiresTypedName &&
      approvalCta.kind === 'approve-completion' ? (
        <Ics204SignDialog
          open={signOpen}
          role={approvalCta.slot.role}
          title={`Sign — ${approvalCta.slot.label}`}
          defaultName={defaultSignName}
          onClose={() => setSignOpen(false)}
          onConfirm={(name) => {
            void confirmSign(name);
          }}
          isPending={adapter.isSaving}
        />
      ) : null}

      {/* Sibling rather than a child, so the two dialogs do not fight over the
          focus trap. */}
      <AlertDialog
        open={showConfirmModal}
        onOpenChange={(open) => {
          if (!open) setShowConfirmModal(false);
        }}
      >
        <AlertDialogContent className="dark">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deactivation</AlertDialogTitle>
            <AlertDialogDescription>
              Deactivated actions will be hidden from the active actions tracker
              but preserved in the system for record-keeping.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              type="button"
              onClick={() => {
                void toggleActive();
              }}
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ActionDrawer;
